import dayjs from "dayjs";
import { SyntheticEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { usePaginationState } from "../../../App/Components/Table/State/PaginationState";
import { useFormDataForClientQuery } from "../../../App/Queries/CampaignQueries";
import { useSelectedClientCustomFields } from "../../../App/Queries/ClientQueries";
import { useSearchTagsQuery } from "../../../App/Queries/FilterQueries";
import {
  useGroupDynamicMembersQuery,
  useGroupProfileQuery,
  useGroupsQuery,
  useSaveGroupMutation,
} from "../../../App/Queries/GroupQueries";
import { useNotification } from "../../../App/State/Hooks/notificationHooks";
import { useDebouncedEffect } from "../../../App/State/Hooks/useDebouncedEffect";
import { useDebouncedHandler } from "../../../App/State/Hooks/useDebouncedHandler";
import { Filter, LocationValue, Rule } from "../../../App/Typings/DynamicGroupRules";
import { getSessionStorage, setSessionStorage } from "../../../App/Utilities/browserStorage";

const savedSessionKey = "dynamic-group-preview";

function useSavedGroupRules(groupId: string) {
  const { data, isLoading, isFetched } = useGroupProfileQuery(groupId, (group) => {
    const formatRules = (rules: Rule[]) =>
      rules.map((rule) => {
        const formattedRule = { ...rule };
        if (
          (formattedRule.filter.type === "interestpicker" || formattedRule.filter.type === "array") &&
          Array.isArray(formattedRule.filter.value.value)
        ) {
          (formattedRule.filter.value as any).value = (formattedRule.filter.value as any).value.join(",");
        }
        if (formattedRule.rules && formattedRule.rules.length > 0) {
          formattedRule.rules = formatRules(formattedRule.rules);
        }
        return formattedRule;
      });
    return {
      rules: formatRules(group.rules),
      groupName: group.name,
      clientId: group.clientId,
      campaignId: group.dynamicGroupCampaignId,
    };
  });

  const rulesDiffer = useCallback(
    (pendingRules: Rule[]) => {
      const compareRules = (rule: Rule, pendingRule: Rule): boolean => {
        const filtersDiffer = !filtersMatch(rule.filter, pendingRule.filter);
        const ruleCountDiffers =
          (!rule.rules && pendingRule.rules) ||
          (rule.rules && !pendingRule.rules) ||
          rule.rules?.length !== pendingRule.rules?.length;

        if (filtersDiffer || ruleCountDiffers) {
          return true;
        }
        return rule.rules?.some((r, i) => compareRules(r, pendingRule.rules[i])) === true;
      };
      if (!data?.rules || data.rules.length !== pendingRules.length) {
        return true;
      }
      return data.rules.some((rule, i) => compareRules(rule, pendingRules[i])) === true;
    },
    [data?.rules],
  );

  return {
    data: {
      rules: data?.rules,
      savedGroupName: data?.groupName ?? "",
      clientId: data?.clientId ?? "",
      campaignId: data?.campaignId ?? "",
    },
    isLoading,
    isFetched,
    rulesDiffer,
  };
}

export function DynamicGroupState() {
  const { id = "" } = useParams();
  const { displayError, displaySuccess } = useNotification();
  const navigate = useNavigate();
  const { data: customFields = [] } = useSelectedClientCustomFields();
  const paginationSessionKey = `${savedSessionKey}-pagination-${id}`;
  const rulesSessionKey = `${savedSessionKey}-rules-${id}`;
  const nameSessionKey = `${savedSessionKey}-name-${id}`;
  const formSessionKey = `${savedSessionKey}-form-${id}`;
  const sessionRules = getSessionStorage<Rule[]>(rulesSessionKey) ?? getEmptyRules();
  const sessionForm = getSessionStorage<string>(formSessionKey);
  const sessionName = getSessionStorage<string>(nameSessionKey);

  const {
    data: { rules: savedRules = getEmptyRules(), savedGroupName, clientId, campaignId },
    isLoading,
    isFetched,
    rulesDiffer,
  } = useSavedGroupRules(id);
  const { data: forms, isLoading: formsLoading } = useFormDataForClientQuery(clientId);
  const { mutateAsync } = useSaveGroupMutation(id);
  const { data: tags, isLoading: tagsLoading } = useSearchTagsQuery();
  const { data: groups, isLoading: isGroupsLoading } = useGroupsQuery(false);

  const [rules, setRules] = useState<Rule[]>(sessionRules);
  const [groupName, setGroupName] = useState<string | undefined>(sessionName ?? undefined);
  const [selectedCampaign, setSelectedCampaign] = useState<string | undefined>(sessionForm ?? undefined);
  const [pendingChanges, setPendingChanges] = useState<boolean>(false);
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);

  const paginationState = usePaginationState(
    {
      sortBy: "createdDate",
      sortDirection: "desc",
      page: 0,
      pageSize: 10,
    },
    paginationSessionKey,
  );
  const paginationQuery = useMemo(
    () => paginationState.getters.getQueryString({ page: paginationState.getters.page + 1 }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [paginationState.getters.page],
  );
  const { data: previewMembers, isLoading: previewLoading } = useGroupDynamicMembersQuery(
    previewOpen ? JSON.stringify(rules) : "",
    selectedCampaign === "none" ? undefined : selectedCampaign,
    paginationQuery,
  );

  const handleGroupSave = useDebouncedHandler(async (e: SyntheticEvent) => {
    e.preventDefault();
    await mutateAsync(
      {
        id,
        name: groupName ?? "",
        rules: JSON.stringify(rules),
        members: [],
        isDynamic: true,
        dynamicGroupCampaignId: selectedCampaign !== "none" ? selectedCampaign : undefined,
      },
      {
        onError: () => {
          displayError("There was an error creating the group. Please try again!");
        },
        onSuccess: () => {
          displaySuccess("Group saved successfully!");
          if (isFetched) {
            localStorage.removeItem(`group-candidates-${id}-custom-table-state`);
            window.location.href = `/candidategroups/${id}`;
          }
        },
      },
    );
  }, 500);

  const handlePreviewClick = (e: SyntheticEvent) => {
    setPreviewOpen(true);
  };

  const handleClosePreview = () => navigate("/candidategroups/" + id);

  const handleSetRules = (pendingRules: Rule[]) => {
    setRules(pendingRules);
    paginationState.setters.setPage(0);
    setSessionStorage<Rule[]>(rulesSessionKey, pendingRules);
  };

  const resetRules = () => {
    handleSetRules(getEmptyRules());
  };

  const resetToSavedRules = () => {
    const newRules =
      savedRules.length > 0 ? (JSON.parse(JSON.stringify(savedRules)) as Rule[]) : getEmptyRules();
    handleSetRules(newRules);
    setSelectedCampaign(campaignId);
    setGroupName(savedGroupName);
  };

  useEffect(() => {
    if (savedGroupName && groupName === undefined) {
      setGroupName(savedGroupName);
    }
  }, [savedGroupName, groupName]);

  useEffect(() => {
    if (campaignId && selectedCampaign === undefined) {
      setSelectedCampaign(campaignId);
    }
  }, [campaignId, selectedCampaign]);

  useDebouncedEffect(
    () => {
      setSessionStorage<string>(nameSessionKey, groupName ?? "");
    },
    [groupName],
    100,
  );
  useDebouncedEffect(
    () => {
      setSessionStorage<string>(formSessionKey, selectedCampaign ?? "");
    },
    [selectedCampaign],
    100,
  );

  useEffect(() => {
    const rulesDifferResult = JSON.stringify(rules) !== JSON.stringify(emptyRules) && rulesDiffer(rules);
    setPendingChanges(rulesDifferResult || campaignId !== selectedCampaign || groupName !== savedGroupName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(rules), rulesDiffer, campaignId, selectedCampaign, groupName, savedGroupName]);

  useEffect(() => {
    const isEmptyRules =
      rules.length === 0 || (rules[0]?.id === "initial-rule" && rules[0]?.filter.command === "");
    const isFormUnchanged = selectedCampaign === campaignId || !selectedCampaign;
    if (isEmptyRules && isFormUnchanged && !isLoading && savedRules.length > 0) {
      setRules(JSON.parse(JSON.stringify(savedRules)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, JSON.stringify(savedRules), JSON.stringify(rules)]);

  return {
    getters: {
      groupId: id,
      campaignId,
      groupName,
      rules,
      isLoading,
      previewLoading,
      previewOpen,
      previewMembers,
      selectedCampaign,
      resetToSavedRules,
      rulesAreValid,
      pendingChanges,
      tags,
      formsLoading,
      tagsLoading,
      paginationState,
      customFields,
      forms,
      groups,
      isGroupsLoading,
    },
    setters: { setGroupName, setPreviewOpen, setRules, setSelectedCampaign, resetRules },
    handlers: {
      handleSetRules,
      handleClosePreview,
      handleGroupSave,
      handlePreviewClick,
    },
  };
}

const emptyRules: Rule[] = [
  {
    filter: {
      command: "",
      field: "",
      type: "",
      value: "",
    },
    id: "initial-rule",
    rules: [],
  },
];

const getEmptyRules = (): Rule[] => {
  return JSON.parse(JSON.stringify(emptyRules));
};

const rulesAreValid = (rules: Rule[]): boolean => {
  for (let i = 0; i < rules.length; i++) {
    if (!ruleIsValid(rules[i])) {
      return false;
    }
    if (rules[i]?.rules?.length > 0) {
      return rulesAreValid(rules[i].rules);
    }
  }

  return true;
};

const ruleIsValid = (rule: Rule): boolean => {
  return rule.filter.command && rule.filter.field && rule.filter.value && isRuleValueValid(rule);
};

const isRuleValueValid = (rule: Rule): boolean => {
  switch (rule.filter.type) {
    case "array":
    case "boolean":
    case "upload":
    case "string":
    case "interestpicker":
      return rule.filter.value.value && rule.filter.value.value !== "";
    case "datetime":
      return validateDateTimeValue(rule);
    case "location":
      return validateLocationValue(rule);
  }
  return false;
};

const validateDateTimeValue = (rule: Rule): boolean => {
  const isRange = rule.filter.command === "between";
  if (isRange) {
    return dayjs(rule.filter.value.value.item1).isValid() && dayjs(rule.filter.value.value.item2).isValid();
  } else {
    return rule.filter.value.value && dayjs(rule.filter.value.value).isValid();
  }
};

const validateLocationValue = (rule: Rule): boolean => {
  const location = rule.filter.value as LocationValue;
  return !!location.name && location.latitude !== 0 && location.longitude !== 0 && location.radius > 0;
};

const filtersMatch = (filter1: Filter, filter2: Filter): boolean => {
  if (
    filter1.type !== filter2.type ||
    filter1.field !== filter2.field ||
    filter1.command !== filter2.command
  ) {
    return false;
  }

  switch (filter1.type) {
    case "array":
    case "boolean":
    case "upload":
    case "string":
    case "interestpicker":
      return filter1.value.value === filter2.value.value;
    case "datetime":
      return (
        filter1.value.value.item1 === filter2.value.value.item1 &&
        filter1.value.value.item2 === filter2.value.value.item2
      );
    case "location":
      return (
        (filter1.value as LocationValue).name === (filter2.value as LocationValue).name &&
        (filter1.value as LocationValue).latitude === (filter2.value as LocationValue).latitude &&
        (filter1.value as LocationValue).longitude === (filter2.value as LocationValue).longitude &&
        (filter1.value as LocationValue).radius === (filter2.value as LocationValue).radius
      );
  }

  return false;
};
